import { motion } from 'framer-motion';
import { IMAGES, SITE, slug } from '@/data';
import { EASE, Reveal } from './Reveal';

function GuestCard({ guest, index }) {
  const href = guest.youtube || '#latest-video';
  return (
    <motion.a
      href={href}
      data-testid={`guest-card-${slug(guest.name)}`}
      initial={{ opacity: 0, y: 34 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, ease: EASE, delay: index * 0.07 }}
      whileHover={{ y: -6 }}
      className="group flex flex-col gap-3.5 rounded-[22px] border border-ivory/10 bg-plum-card p-3.5 transition-[border-color,box-shadow] duration-300 hover:border-gold/50 hover:shadow-[0_18px_50px_rgba(0,0,0,0.4)]"
    >
      <div className="spotlight relative aspect-[4/5] overflow-hidden rounded-2xl bg-plum-deep">
        {guest.photo ? (
          <img
            src={guest.photo}
            alt={`${guest.name} in the car`}
            loading="lazy"
            width="800"
            height="1000"
            className="h-full w-full object-cover [object-position:85%_center] transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="relative h-full w-full">
            <img
              src={IMAGES.carNight}
              alt=""
              loading="lazy"
              className="h-full w-full scale-105 object-cover opacity-40 grayscale transition-transform duration-500 group-hover:scale-110"
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
              <span className="font-display text-7xl text-transparent opacity-90 [-webkit-text-stroke:1.5px_rgba(232,184,106,0.55)]">
                {guest.name.charAt(0)}
              </span>
              <span className="px-4 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-ivory/60">
                [PHOTO · DROPPING SOON]
              </span>
            </div>
          </div>
        )}
        <span className="absolute left-3.5 top-3.5 rounded-full border border-ivory/15 bg-plum-deep/70 px-3 py-1 font-mono text-[10px] font-bold tracking-[0.2em] text-gold backdrop-blur-md">
          EP.{String(index + 1).padStart(2, '0')}
        </span>
      </div>
      <div className="px-1 pb-1.5">
        <div className="font-display text-2xl leading-tight tracking-wide text-ivory">{guest.name}</div>
        <div className="mt-1 text-[14px] font-bold text-sky transition-colors duration-200 group-hover:text-gold">
          {guest.youtube ? 'Watch + Top 10 →' : 'Top 10 →'}
        </div>
      </div>
    </motion.a>
  );
}

export default function CarPool() {
  return (
    <section id="carpool" className="mx-auto max-w-[1280px] scroll-mt-24 px-6 pt-24 md:pt-32" data-testid="carpool-section">
      <Reveal>
        <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
          <div>
            <span className="font-mono text-[13px] font-bold uppercase tracking-[0.25em] text-mauve">The car pool archive</span>
            <h2 className="mt-3 font-display text-5xl uppercase leading-[0.95] text-ivory md:text-7xl">
              WHO'S BEEN IN <span className="text-mauve">THE CAR</span>
            </h2>
          </div>
          <p className="max-w-[400px] text-lg text-ivory/65">
            Every episode, every Top 10, every story from the passenger seat.
          </p>
        </div>
      </Reveal>

      <div className="grid grid-cols-2 gap-4 md:gap-5 lg:grid-cols-4">
        {SITE.guests.map((g, i) => (
          <GuestCard key={g.name} guest={g} index={i} />
        ))}

        <motion.a
          href="#next"
          data-testid="guest-whos-next-card"
          initial={{ opacity: 0, y: 34 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.35 }}
          whileHover={{ y: -6 }}
          className="col-span-2 flex min-h-[280px] flex-col items-center justify-center gap-3 rounded-[22px] border-2 border-dashed border-gold/60 bg-gold/5 p-6 text-center transition-[border-color,background-color] duration-300 hover:border-gold hover:bg-gold/10 lg:col-span-2"
        >
          <span className="font-display text-4xl leading-[1.02] tracking-wide text-ivory md:text-5xl">
            WHO'S<br />NEXT?
          </span>
          <span className="text-[15px] text-ivory/65">You pick the next passenger</span>
          <span className="font-extrabold text-gold">Tell us who →</span>
        </motion.a>
      </div>
    </section>
  );
}
